import { useCallback, useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { every, filter, has, maxBy } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { t } from 'i18next';

import { API_CONFIG, STROKESCOLORS, colorPallet } from 'shared/constants/constants';
import { State } from 'shared/interface';
import {
	chartTimeRangeMapperForNumber,
	customNumberFormatter,
	defineChartMargins,
	getDataTypeDisplayName,
	replaceHashtagFromUrl
} from 'shared/util/utility';
import { Loader } from 'shared/components/spinner/spinner';
import { ChartAxisPosition, ScaleType } from 'shared/interface/enums';
import httpService from 'shared/services/http.service';
import localizationConstants from 'shared/util/translation/constants';
import { useWhitelabelConfigStatus } from 'shared/hooks/useWhitelabelConfigStatus';

import StockChart from 'features/futuretable/component/stockChart';
import {
	IAdvanceChartConfigType,
	IChartData,
	IChartDesignConfig,
	IChartDetails,
	IChartItem,
	IChartPayloadType,
	IChartSettingsType
} from 'features/futuretable/interface/futureTable';
import { IndicatorsDropdown, indicatorRange, indicatorsWindowWidth } from 'features/futuretable/util/chartUtility';
import DashboardCardHeader from './dashboardCardHeader';
import { Translate } from 'shared/util/translation/translate';

interface Props {
	deleteData: (data: string, action: boolean) => void;
	chartInfo: any;
	cardIndex: number;
}

let mainChartData: { [key: string]: IChartData[] } = {};
let dataForIndicators: {
	[key: string]: {
		[key: string]: IChartData[];
	};
} = {};
let chartDetails: {
	[key: string]: {
		[key: string]: IChartDetails;
	};
} = {};
let advanceChartConfig: { [key: string]: IAdvanceChartConfigType } = {};
const ChartComponent = (props: Props) => {
	const { material_status } = useWhitelabelConfigStatus();
	const navigate = useNavigate();
	const params = useParams();
	const location = useLocation();
	const { chartInfo } = props;
	const { language, chartView } = useSelector((state: State) => ({ ...state.siteConfig }));
	const [chartData, setChartData] = useState<IChartData[]>([]);
	const [chartDesignConfig, setChartDesignConfig] = useState({} as { [key: string]: IChartDesignConfig });
	const [strokeColors, setStrokeColors] = useState(STROKESCOLORS);
	const [separateLoading, setSeparateLoading] = useState({} as { [key: string]: boolean });
	const [rearrangedData, setRearrangedData] = useState([] as any);
	const chartSelection = chartInfo.chartConfig.chartSelection;
	const [updatedChartSettings, setUpdatedChartSettings] = useState<IChartSettingsType[]>([]);

	const [barWidth, setBarWidth] = useState<{ [key: string]: number }>({});
	const yValues = useMemo(
		() =>
			chartData.flatMap((item: IChartData) =>
				Object.keys(item)
					.filter((key) => key.startsWith('cv') && (item[key] as IChartItem)?.l !== undefined)
					.map((key) => (item[key] as IChartItem).l)
			),
		[chartData]
	);
	const maxDigits = useMemo(
		() =>
			Math.max(
				0,
				...yValues.map((v: number) => {
					if (v === null || v === undefined) return 0;
					return v.toString().replace(/[^0-9]/g, '').length;
				})
			),
		[yValues]
	);
	const fontSize = useMemo(() => {
		let size = props.cardIndex >= 2 ? 10 : 12;
		if (maxDigits >= 7) size = 8;
		return size;
	}, [props.cardIndex, maxDigits]);

	useEffect(() => {
		mainChartData = {
			...mainChartData,
			[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`]:
				mainChartData[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`] || []
		};
		dataForIndicators = {
			...dataForIndicators,
			[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`]:
				dataForIndicators[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`] || []
		};
		advanceChartConfig = {
			...advanceChartConfig,
			[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`]: advanceChartConfig[
				`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`
			] || {
				percentageChange:
					chartView === ScaleType.PERCENTAGE_CHANGE ||
					chartView === ScaleType.PERCENTAGE_CHANGE_TOTAL ||
					chartView === ScaleType.PERCENTAGE_WITH_LOGARITHMIC,
				logarithmicYAxis: chartView === ScaleType.LOGARITHAMIC_AXIS || chartView === ScaleType.PERCENTAGE_WITH_LOGARITHMIC,
				scaleType: chartSelection.chartView || chartView,
				chartSettings: []
			}
		};
		setStrokeColors({ ...strokeColors, ...chartInfo.chartConfig.strokeColors });
		fetchChartData();
	}, [chartInfo, chartSelection.chartView]);

	const chartOrder = useCallback(
		(data: any) => {
			const chartSettings = advanceChartConfig[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`].chartSettings;
			const convertedArray: any[] = [];
			let globalIndex = 0;

			chartSettings.forEach((item, settingIndex) => {
				// Handle multiple fields per material
				const fieldsCount = item.fields && item.fields.length > 0 ? item.fields.length : 1;

				for (let fieldIndex = 0; fieldIndex < fieldsCount; fieldIndex++) {
					const dataKey = `cv${item.symbol}-${item.boerse}-${globalIndex}`;
					const maxPrice = maxBy(data, (d: any) => d[dataKey]?.l);
					convertedArray.push({
						oldKey: dataKey,
						value: maxPrice,
						index: globalIndex,
						symbol: item.symbol,
						boerse: item.boerse,
						settingIndex: settingIndex,
						fieldIndex: fieldIndex
					});
					globalIndex++;
				}
			});
			return convertedArray;
		},
		[advanceChartConfig]
	);

	const getWidestLabelPixelWidth = useCallback((values: number[], font = '12px Arial') => {
		const canvas = document.createElement('canvas');
		const ctx = canvas.getContext('2d');
		if (!ctx) return 0;
		ctx.font = font;
		return Math.max(...values.map((v) => ctx.measureText(customNumberFormatter(v)).width));
	}, []);

	const fetchChartData = async () => {
		const isIframe = location.pathname.includes('iframe');
		try {
			setSeparateLoading({
				...separateLoading,
				[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`]: true
			});
			const chartIndicators = chartSelection.indicator.map((indi: any) => {
				if (typeof indi === 'string') {
					if (indi === 'sto') {
						return {
							...indicatorRange.sto,
							value: indi,
							label: IndicatorsDropdown.find((value) => value.value === indi)?.label || '',
							symbol: chartInfo.symbol,
							boerse: chartInfo.boerse
						};
					} else if (indi === 'macd') {
						return {
							...indicatorRange.macd,
							value: indi,
							label: IndicatorsDropdown.find((value) => value.value === indi)?.label || '',
							symbol: chartInfo.symbol,
							boerse: chartInfo.boerse
						};
					} else {
						return {
							value: indi,
							label: IndicatorsDropdown.find((value) => value.value === indi)?.label || '',
							symbol: chartInfo.symbol,
							boerse: chartInfo.boerse,
							calculatedValue: indicatorsWindowWidth[indi]
						};
					}
				} else if (typeof indi === 'object') {
					return { ...indi };
				}
			});

			const axisPosition = has(chartSelection, 'axis')
				? chartSelection.axis
				: chartView === ScaleType.PERCENTAGE_CHANGE || chartView === ScaleType.PERCENTAGE_CHANGE_TOTAL
				? 'left'
				: 'right';
			const chartSettingsFromComparativeArray = [
				{
					id: uuid(),
					name: '',
					symbol: chartInfo.symbol,
					boerse: chartInfo.boerse,
					unit: { label: chartSelection?.unit || '', value: chartSelection?.unit || '' },
					currency: {
						label: chartSelection.currency || '',
						value: chartSelection.currency || ''
					},
					indicators:
						!has(chartInfo.chartConfig, 'advanceChartConfig') || chartSelection.indicator.length > 0
							? chartIndicators
							: chartInfo.chartConfig.advanceChartConfig.chartSettings[0].indicators,
					history: {
						label: chartSelection?.history || t(localizationConstants.none),
						value: chartSelection?.history || ''
					},
					axis: {
						value: axisPosition,
						label: has(chartSelection, 'axis')
							? chartSelection.axis.charAt(0).toUpperCase() + chartSelection.axis.slice(1)
							: chartView === ScaleType.PERCENTAGE_CHANGE || chartView === ScaleType.PERCENTAGE_CHANGE_TOTAL
							? 'Left'
							: 'Right'
					},
					color: chartSelection.chartColor || colorPallet[0],
					forecastedColor: chartInfo?.chartConfig?.advanceChartConfig?.chartSettings[0]?.forecastedColor || [],
					fields: chartInfo?.chartConfig?.advanceChartConfig?.chartSettings[0]?.fields || []
				}
			] as IChartSettingsType[];
			if (chartInfo.chartConfig.comparativeChartIndicator.length > 0) {
				chartInfo.chartConfig.comparativeChartIndicator.map((chart: any) => {
					chartSettingsFromComparativeArray.push({
						id: chart?.id || uuid(),
						name: '',
						symbol: chart.symbol,
						boerse: chart.boerse,
						unit: { label: chart.unit?.value || '', value: chart.unit?.value || '' },
						currency: { label: chart.waehrung || chart.currency?.label || '', value: chart.waehrung || chart.currency?.value || '' },
						indicators: chart.indicators || [],
						history: { label: chart?.history?.value || t(localizationConstants.none), value: chart?.history?.value || '' },
						axis: chart.axis || { value: 'right', label: 'Right' },
						color: chart.color || colorPallet[chartSettingsFromComparativeArray.length],
						disallowed: chart.disallowed,
						basis_symbol: chart.basis_symbol,
						fields: chart.fields || []
					});
				});
			}
			advanceChartConfig = {
				...advanceChartConfig,
				[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`]: {
					...advanceChartConfig[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`],
					scaleType: chartSelection.chartView || chartView,
					chartSettings: [...chartSettingsFromComparativeArray]
				}
			};

			let payload: IChartPayloadType = {
				data: advanceChartConfig[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`].chartSettings
					.map((chart: IChartSettingsType) => {
						// If fields array exists and has items, create separate entries for each field
						if (chart.fields && chart.fields.length > 0) {
							return chart.fields.map((field: string) => {
								return {
									id: chart?.id,
									symbol: chart?.symbol,
									boerse: chart?.boerse,
									waehrung: chart.currency.value,
									unit: chart.unit?.value || '',
									year: parseInt(chart.history.value) || '',
									field: getDataTypeDisplayName(field)
								};
							});
						} else {
							// Default behavior when no fields are selected
							return {
								id: chart?.id,
								symbol: chart?.symbol,
								boerse: chart?.boerse,
								waehrung: chart.currency.value,
								unit: chart.unit?.value || '',
								year: parseInt(chart.history.value) || ''
							};
						}
					})
					.flat(), // Flatten the array since we might have nested arrays
				percentageTotal: chartView === ScaleType.PERCENTAGE_CHANGE_TOTAL
			};
			const customChartSpan = chartInfo.chartConfig.customChartSpan;
			if (
				customChartSpan?.von !== undefined &&
				customChartSpan?.bis !== undefined &&
				customChartSpan?.von !== '' &&
				customChartSpan?.bis !== ''
			) {
				payload = {
					...payload,
					start_date: customChartSpan?.von,
					end_date: customChartSpan?.bis
				};
			} else {
				payload = {
					...payload,
					span:
						chartSelection.type === 'bar'
							? 'max'
							: chartTimeRangeMapperForNumber(
									chartData,
									typeof chartSelection.span === 'string' ? chartSelection.span : chartSelection.span.toString()
							  )
				};
			}
			const response = await httpService.post(
				isIframe ? `${API_CONFIG.path.getIFrameChartData}/${params?.id}` : API_CONFIG.path.getChartData6,
				payload,
				{},
				{ isAccessTokenRequire: !isIframe }
			);

			const cleanedData = response.data.map((row: IChartData) => {
				const cleanedRow = { ...row };

				Object.keys(cleanedRow).forEach((key) => {
					if (key.startsWith('cv')) {
						const val = cleanedRow[key];

						const isEmptyObject = typeof val === 'object' && val !== null && Object.keys(val).length === 0;
						const allNull = typeof val === 'object' && val !== null && Object.values(val).every((v) => v === null);
						const noValidValue =
							typeof val === 'object' &&
							val !== null &&
							!['e', 'l', 't', 'o', 'h'].some((k) => {
								const item = val as IChartItem;
								const value = k === 'e' ? item.e : k === 'l' ? item.l : k === 't' ? item.t : k === 'o' ? item.o : item.h;
								return typeof value === 'number' && !isNaN(value);
							});

						if (val === null || isEmptyObject || allNull || noValidValue) {
							delete cleanedRow[key];

							const suffix = key.replace(/^cv/, '');
							delete cleanedRow[`price${suffix}`];
							delete cleanedRow[`percentage${suffix}`];
						}
					}
				});

				return cleanedRow;
			});

			const chartHighValues = chartOrder(cleanedData);
			const sortedData = [...chartHighValues].sort((a, b) => a.value - b.value);

			const newData = sortedData.map((entry, index) => ({
				...entry,
				newIndex: index,
				updatedKey: `cv${entry.symbol}-${entry.boerse}-${index}`
			}));

			cleanedData.forEach((objToUpdate: IChartData) => {
				newData.forEach(({ oldKey, updatedKey }) => {
					if (Object.prototype.hasOwnProperty.call(objToUpdate, oldKey)) {
						objToUpdate[updatedKey] = objToUpdate[oldKey];
						if (oldKey !== updatedKey) {
							delete objToUpdate[oldKey];
						}
					}
				});
			});
			response.data.forEach((objToUpdate: any) => {
				newData.forEach((update) => {
					const { oldKey, updatedKey } = update;
					if (Object.prototype.hasOwnProperty.call(objToUpdate, oldKey)) {
						objToUpdate[updatedKey] = objToUpdate[oldKey];
						if (oldKey !== updatedKey) {
							delete objToUpdate[oldKey];
						}
					}
				});
			});
			advanceChartConfig = {
				...advanceChartConfig,
				[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`]: {
					...advanceChartConfig[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`],
					scaleType: chartSelection.chartView || chartView,
					chartSettings: advanceChartConfig[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`].chartSettings.map(
						(chart) => {
							// Find the first header entry for this material to get basic info
							const firstHeaderKey = Object.keys(response.headers).find((key) => key.startsWith(`${chart.symbol}-${chart.boerse}-`));
							const headerData = firstHeaderKey ? response.headers[firstHeaderKey] : null;

							return {
								...chart,
								name: (language === 'en' && headerData?.name_eng !== '' && headerData?.name_eng) || headerData?.name || '',
								unit: {
									label: headerData?.einheit || '',
									value: headerData?.einheit || ''
								},
								currency: {
									label: headerData?.waehrung || '',
									value: headerData?.waehrung || ''
								},
								basis_symbol: headerData?.basis_symbol || ''
							};
						}
					)
				}
			};
			mainChartData = {
				...mainChartData,
				[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`]: cleanedData
			};
			setRearrangedData(newData);
			setChartData(mainChartData[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`]);

			const tempPayload: any = [];
			const tempObj: { [key: string]: IChartDetails } = {};
			for (let i = 0; i < newData.length; i++) {
				const { index, symbol, boerse, newIndex } = newData[i];
				tempPayload.push(payload.data[index]);
				tempObj[`${symbol}-${boerse}-${newIndex}`] = response.headers[`${symbol}-${boerse}-${index}`];
			}
			chartDetails = {
				...chartDetails,
				[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`]: { ...tempObj }
			};

			// 1. Get all y-axis values
			const yValues = response.data.flatMap((item: IChartData) =>
				Object.keys(item)
					.filter((key) => key.startsWith('cv') && (item[key] as IChartItem)?.l !== undefined)
					.map((key) => (item[key] as IChartItem).l)
			);

			// 3. Measure max label width in pixels
			const labelWidth = getWidestLabelPixelWidth(yValues); // e.g., might be 65

			// 4. Get base margin
			const baseMargins = defineChartMargins(
				advanceChartConfig[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`].chartSettings,
				true
			);

			setChartDesignConfig({
				...chartDesignConfig,
				[`${chartInfo.symbol}${props.cardIndex}`]: {
					margin: {
						left: baseMargins?.left as number,
						right: Math.ceil(labelWidth + 8) + 16,
						top: 10,
						bottom: props.cardIndex > 1 ? 30 : 40
					},
					fontSize: props.cardIndex >= 2 ? 10 : 12,
					rectWidth: props.cardIndex >= 2 ? 40 : 55,
					rectHeight: props.cardIndex >= 2 ? 15 : 20
				}
			});
			setSeparateLoading({
				...separateLoading,
				[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`]: false
			});
		} catch (error) {
			console.error('error', error);
			setSeparateLoading({
				...separateLoading,
				[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`]: false
			});
		}
	};

	const currency = isEmpty(chartSelection.currency) ? 'EUR' : chartSelection.currency;
	const isLeftAxis =
		filter(advanceChartConfig[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`]?.chartSettings, {
			axis: { value: ChartAxisPosition.LEFT_AXIS }
		}).length > 0;
	const isRightAxis =
		filter(advanceChartConfig[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`]?.chartSettings, {
			axis: { value: ChartAxisPosition.RIGHT_AXIS }
		}).length > 0;
	const chartDetailsIndex = rearrangedData.filter((item: any) => {
		return chartInfo.boerse === item.boerse && chartInfo.symbol === item.symbol;
	});
	useEffect(() => {
		const chartSettings = advanceChartConfig[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`].chartSettings;
		const tempArray = [];
		if (!isEmpty(rearrangedData)) {
			for (let i = 0; i < rearrangedData.length; i++) {
				const { index } = rearrangedData[i];
				if (chartSettings[index]) {
					tempArray.push(chartSettings[index]);
				}
			}
		}
		setUpdatedChartSettings(tempArray);
	}, [chartInfo.boerse, chartInfo.symbol, rearrangedData]);

	const onMouseEnter = () => {
		const pageWrapper = document.getElementById('dashboard-scrollable--wrapper');
		if (pageWrapper) {
			pageWrapper.style.overflow = 'hidden';
		}
	};
	const onMouseLeave = () => {
		const pageWrapper = document.getElementById('dashboard-scrollable--wrapper');
		if (pageWrapper) {
			pageWrapper.style.overflow = 'auto';
		}
	};

	const navigateToPage = useCallback(() => {
		const navigateUrl = !isEmpty(chartInfo.chartConfig.comparativeChartIndicator)
			? `/materials/chartScreen?timeSpan=${chartSelection.span}&currency=${currency}&element_id=${props.chartInfo.element_id}`
			: `/materials/futureTable?element_id=${props.chartInfo.element_id}&boerse=${replaceHashtagFromUrl(props.chartInfo.boerse)}&basis_symbol=${
					props.chartInfo.basis_symbol
			  }&materialParentId=${0}&symbol=${replaceHashtagFromUrl(props.chartInfo.symbol)}&currency=${currency}&isRedirectChartScreen=${true}`;
		if (material_status === 'hideall') {
			return;
		} else {
			navigate(navigateUrl, {
				state: {
					chartSelection: { ...chartSelection, customChartSpan: chartInfo.chartConfig.customChartSpan },
					customChartSpan: chartInfo.chartConfig.customChartSpan,
					comparativeChartIndicator: chartInfo.chartConfig.comparativeChartIndicator || [],
					strokeColors: chartInfo.chartConfig.strokeColors,
					disallowed:
						chartDetails[chartInfo.symbol + '-' + chartInfo.boerse + '-' + chartInfo.element_id][
							`${chartInfo.symbol}-${chartInfo.boerse}-0`
						]?.disallowed,
					advanceChartConfig: advanceChartConfig[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`],
					name: updatedChartSettings.length > 1 ? (language === 'en' && props?.chartInfo?.name_eng) || props?.chartInfo?.name : ''
				}
			});
		}
	}, [
		chartInfo.boerse,
		chartInfo.chartConfig.comparativeChartIndicator,
		chartInfo.chartConfig.customChartSpan,
		chartInfo.chartConfig.strokeColors,
		chartInfo.element_id,
		chartInfo.symbol,
		chartSelection,
		currency,
		material_status,
		navigate,
		props.chartInfo.basis_symbol,
		props.chartInfo.boerse,
		props.chartInfo.element_id,
		props.chartInfo.name,
		props.chartInfo.symbol,
		updatedChartSettings.length
	]);

	const filteredChartData = useMemo(() => {
		if (chartSelection.type !== 'bar') {
			return chartData
				.map((dataPoint) => {
					const newDataPoint = { ...dataPoint };
					Object.keys(newDataPoint).forEach((key) => {
						const value = newDataPoint[key];
						if (
							key.startsWith('cv') &&
							value &&
							typeof value === 'object' &&
							!Array.isArray(value) &&
							'source_data' in value &&
							value.source_data &&
							typeof value.source_data === 'object'
						) {
							const newSourceData = { ...value.source_data };
							(chartSelection.forecastKeys || []).forEach((symbol: string) => {
								delete newSourceData[symbol];
							});
							if (Object.keys(newSourceData).length === 0) {
								delete newDataPoint[key];
							} else {
								newDataPoint[key] = {
									...value,
									source_data: newSourceData
								};
							}
						}
					});
					return newDataPoint;
				})
				.filter((dataPoint) => {
					return Object.keys(dataPoint).some((key) => key.startsWith('cv'));
				});
		} else {
			// For bar chart: Only keep actual and selected forecast key(s)
			return chartData
				.map((dataPoint) => {
					const newDataPoint = { ...dataPoint };
					Object.keys(newDataPoint).forEach((key) => {
						const value = newDataPoint[key];
						if (
							key.startsWith('cv') &&
							value &&
							typeof value === 'object' &&
							!Array.isArray(value) &&
							'source_data' in value &&
							value.source_data &&
							typeof value.source_data === 'object'
						) {
							const allowedKeys = ['actual', ...(chartSelection.forecastKeys || [])];
							const filteredSourceData: any = {};
							allowedKeys.forEach((k) => {
								if (value.source_data[k] !== undefined) {
									filteredSourceData[k] = value.source_data[k];
								}
							});
							if (Object.keys(filteredSourceData).length === 0) {
								delete newDataPoint[key];
							} else {
								newDataPoint[key] = {
									...value,
									source_data: filteredSourceData
								};
							}
						}
					});
					return newDataPoint;
				})
				.filter((dataPoint) => {
					return Object.keys(dataPoint).some((key) => key.startsWith('cv'));
				});
		}
	}, [chartData, chartSelection.forecastKeys, chartSelection.type]);

	return (
		<>
			{!isEmpty(chartDetails[chartInfo.symbol + '-' + chartInfo.boerse + '-' + chartInfo.element_id]) && (
				<DashboardCardHeader
					name={language === 'de' ? props.chartInfo.name : props.chartInfo.name_eng || props.chartInfo.name}
					action={true}
					deleteData={props.deleteData}
					elementId={props.chartInfo.element_id}
					section='chart'
					advanceChartConfig={{ ...advanceChartConfig[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`] }}
					updatedChartSettings={updatedChartSettings}
					cardIndex={props.cardIndex}
					navigateToPage={navigateToPage}
					chartDetails={chartDetails[chartInfo.symbol + '-' + chartInfo.boerse + '-' + chartInfo.element_id]}
					loading={separateLoading[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`]}
				/>
			)}
			<div
				className={`chart-wrapper ${isLeftAxis && isRightAxis ? 'left-right-axis' : isRightAxis ? 'right-axis' : ''}`}
				id={`chart-container-dashboard-${chartInfo.symbol}-${props.cardIndex}`}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
			>
				{separateLoading[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`] && <Loader className='pt-10' />}
				{!separateLoading[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`] &&
					!isEmpty(chartDetails[chartInfo.symbol + '-' + chartInfo.boerse + '-' + chartInfo.element_id]) &&
					isEmpty(
						Object.values(chartDetails[chartInfo.symbol + '-' + chartInfo.boerse + '-' + chartInfo.element_id]).filter(
							(item) => item?.disallowed === 0
						)
					) && <div className='d-flex justify-content-center align-items-center locked-chart' style={{ height: '100%' }} />}
				{isEmpty(chartData) &&
					!separateLoading[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`] &&
					!isEmpty(chartDetails[chartInfo.symbol + '-' + chartInfo.boerse + '-' + chartInfo.element_id]) &&
					every(Object.values(chartDetails[chartInfo.symbol + '-' + chartInfo.boerse + '-' + chartInfo.element_id]), ['disallowed', 0]) && (
						<p className='no-result-title pt--20 text-align-center font--20px'>
							<Translate text={localizationConstants.noDataFound} />
						</p>
					)}
				{!isEmpty(chartData) &&
					chartDetailsIndex &&
					!separateLoading[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`] &&
					!isEmpty(updatedChartSettings) &&
					!isEmpty(chartDetails[chartInfo.symbol + '-' + chartInfo.boerse + '-' + chartInfo.element_id]) &&
					!isEmpty(
						Object.values(chartDetails[chartInfo.symbol + '-' + chartInfo.boerse + '-' + chartInfo.element_id]).filter(
							(item) => item?.disallowed === 0
						)
					) &&
					!isEmpty(advanceChartConfig[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`]) && (
						<StockChart
							chartData={filteredChartData}
							chartType={
								chartSelection.chartView === ScaleType.PERCENTAGE_CHANGE ||
								chartSelection.chartView === ScaleType.PERCENTAGE_CHANGE_TOTAL
									? 'line'
									: chartSelection.type
							}
							showZoomButton={false}
							loading={separateLoading[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`]}
							chartDetails={
								chartDetails[chartInfo.symbol + '-' + chartInfo.boerse + '-' + chartInfo.element_id][
									`${chartInfo.symbol}-${chartInfo.boerse}-${[chartDetailsIndex[0].newIndex]}`
								]
							}
							cardIndex={props.cardIndex}
							chartDesignConfig={chartDesignConfig[`${chartInfo.symbol}${props.cardIndex}`]}
							spanObj={{
								span: chartTimeRangeMapperForNumber(
									chartData,
									typeof chartSelection.span === 'string' ? chartSelection.span : chartSelection.span.toString()
								)
							}}
							chartName='dashboard'
							chartSettings={updatedChartSettings}
							scaleType={advanceChartConfig[`${chartInfo.symbol}-${chartInfo.boerse}-${chartInfo.element_id}`].scaleType}
							barWidth={barWidth}
							setBarWidth={setBarWidth}
							allChartDetails={chartDetails[chartInfo.symbol + '-' + chartInfo.boerse + '-' + chartInfo.element_id]}
							forecastKeys={chartInfo?.chartConfig?.advanceChartConfig?.forecastKeys || chartSelection?.forecastKeys || []}
							xAxisFontSize={12}
							yAxisFontSize={fontSize}
							selectedDataTypesPerMaterial={chartInfo?.chartConfig?.chartSelection?.selectedDataTypesPerMaterial || {}}
							selectedFrequencies={chartInfo?.chartConfig?.chartSelection?.selectedFrequencies || {}}
						/>
					)}
			</div>
		</>
	);
};

export default ChartComponent;
